/**
 * Utility functions for bug reporting and feedback
 */

export interface BugReportContext {
  pageName: string;
  pageUrl: string;
  userRole?: string;
  organisationName?: string;
  labName?: string;
}

/**
 * Detect browser name and version
 */
export const detectBrowser = (): string => {
  const userAgent = navigator.userAgent;
  let browser = "Unknown";
  let version = "";

  // Chrome
  if (userAgent.indexOf("Chrome") > -1 && userAgent.indexOf("Edg") === -1) {
    browser = "Chrome";
    const match = userAgent.match(/Chrome\/(\d+)/);
    version = match ? (match[1] ?? "") : "";
  }
  // Edge
  else if (userAgent.indexOf("Edg") > -1) {
    browser = "Edge";
    const match = userAgent.match(/Edg\/(\d+)/);
    version = match ? (match[1] ?? "") : "";
  }
  // Firefox
  else if (userAgent.indexOf("Firefox") > -1) {
    browser = "Firefox";
    const match = userAgent.match(/Firefox\/(\d+)/);
    version = match ? (match[1] ?? "") : "";
  }
  // Safari
  else if (
    userAgent.indexOf("Safari") > -1 &&
    userAgent.indexOf("Chrome") === -1
  ) {
    browser = "Safari";
    const match = userAgent.match(/Version\/(\d+)/);
    version = match ? (match[1] ?? "") : "";
  }

  return version ? `${browser} ${version}` : browser;
};

/**
 * Detect operating system
 */
export const detectOS = (): string => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;

  // Windows
  if (userAgent.indexOf("Win") > -1) {
    if (userAgent.indexOf("Windows NT 10.0") > -1) return "Windows 10/11";
    if (userAgent.indexOf("Windows NT 6.3") > -1) return "Windows 8.1";
    if (userAgent.indexOf("Windows NT 6.2") > -1) return "Windows 8";
    if (userAgent.indexOf("Windows NT 6.1") > -1) return "Windows 7";
    return "Windows";
  }

  // macOS
  if (userAgent.indexOf("Mac") > -1) {
    if (platform.indexOf("iPhone") > -1) return "iOS (iPhone)";
    if (platform.indexOf("iPad") > -1) return "iOS (iPad)";
    return "macOS";
  }

  // Linux
  if (userAgent.indexOf("Linux") > -1) {
    if (userAgent.indexOf("Android") > -1) return "Android";
    return "Linux";
  }

  // iOS (fallback)
  if (userAgent.indexOf("iPhone") > -1) return "iOS (iPhone)";
  if (userAgent.indexOf("iPad") > -1) return "iOS (iPad)";

  return "Unknown OS";
};

/**
 * Get current timezone
 */
export const getTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "Unknown";
  }
};

/**
 * Format current date and time
 */
export const getCurrentDateTime = (): string => {
  const now = new Date();
  return now.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

/**
 * Build email subject
 */
export const buildEmailSubject = (pageName: string): string => {
  return `Support request: ${pageName}`;
};

/**
 * Build email body with template
 */
export const buildEmailBody = (context: BugReportContext): string => {
  const browser = detectBrowser();
  const os = detectOS();
  const timezone = getTimezone();
  const dateTime = getCurrentDateTime();

  const template = `Page / screen:

Page name: ${context.pageName}
Page URL: ${context.pageUrl}

What I was trying to do:

[write here]

What I expected:

[write here]

What happened instead:

[write here]

Error message / code (if any):

[paste here]

Screenshot or screen recording attached:

Yes / No

Device + browser:

Operating system: ${os}
Browser: ${browser}
Browser version (if known): [included above]

Account context:

User role: ${context.userRole || "[your role]"}
Organisation name (if relevant): ${context.organisationName || "[your organisation]"}
Lab name (if relevant): ${context.labName || "[your lab]"}

Timing:

Date/time issue occurred (local): ${dateTime}
Timezone: ${timezone}

Extra notes:

Does it happen every time or just once? [write here]
Was this working before? Yes / No / Not sure

`;

  return template;
};

/**
 * Generate mailto link
 */
export const generateMailtoLink = (context: BugReportContext): string => {
  const email = "support@labzero.eco";
  const subject = buildEmailSubject(context.pageName);
  const body = buildEmailBody(context);

  // Encode subject and body for URL
  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);

  return `mailto:${email}?subject=${encodedSubject}&body=${encodedBody}`;
};

/**
 * Open mailto link (returns false if likely failed)
 */
export const openMailtoLink = (mailtoUrl: string): boolean => {
  try {
    window.location.href = mailtoUrl;
    return true;
  } catch (error) {
    console.error("Failed to open mailto link:", error);
    return false;
  }
};
